// ============================================================================
// promptObjectLibrary.js – 2024‑2026 Editorial Edition
// Central vocabulary & builders for interior design image generation.
// Curated to produce high‑end, magazine‑quality, cozy interior prompts.
// ============================================================================

// ============================================
//  CUSTOM / FOR MANUAL INTERNAL TEAM EDITING
//  Use the same label format: "category: term"
// ============================================
export const CUSTOM_COLORS = [
  // "color: custom coral", "color: trend moss"
];

export const CUSTOM_MATERIALS = [
  // "material: proprietary resin", "texture: signature rib"
];

export const CUSTOM_FURNITURE = [
  // "furniture: studio chaise"
];

export const CUSTOM_DECOR = [
  // "decor: limited edition vase"
];

// ============================================
//  COLOR LIBRARY – color‑theory fundamentals + 2024‑2026 trend palette
// ============================================
export const COLOR_LIBRARY = [
  // Primary
  "color: primary red",
  "color: primary blue",
  "color: primary yellow",
  // Secondary
  "color: secondary green",
  "color: secondary orange",
  "color: secondary violet",
  // Tertiary (intermediate)
  "color: tertiary yellow‑green",
  "color: tertiary yellow‑orange",
  "color: tertiary red‑orange",
  "color: tertiary red‑violet",
  "color: tertiary blue‑violet",
  "color: tertiary blue‑green",

  // Neutrals
  "color: pure white",
  "color: warm white",
  "color: ivory",
  "color: cream",
  "color: beige",
  "color: greige",
  "color: taupe",
  "color: sand",
  "color: stone",
  "color: charcoal gray",
  "color: dove gray",
  "color: slate gray",
  "color: true black",
  "color: soft black",

  // Browns & earth
  "color: espresso",
  "color: walnut",
  "color: camel",
  "color: tan",
  "color: terracotta",
  "color: rust",
  "color: ochre",
  "color: cognac",

  // Deep accent & jewel tones
  "color: navy blue",
  "color: indigo",
  "color: cobalt",
  "color: cerulean",
  "color: sky blue",
  "color: emerald green",
  "color: forest green",
  "color: olive green",
  "color: sage",
  "color: eucalyptus",
  "color: crimson",
  "color: burgundy",
  "color: oxblood",
  "color: dusty rose",
  "color: blush",
  "color: lavender",
  "color: lilac",
  "color: plum",
  "color: aubergine",
  "color: butter yellow",
  "color: marigold",
  "color: saffron",
  "color: tangerine",
  "color: apricot",
  "color: peach",
  "color: teal",
  "color: peacock",
  "color: turquoise",
  "color: mustard",
  "color: coral",
  "color: ruby",
  "color: amethyst",
  "color: sapphire blue",
  "color: moss",
  "color: chocolate brown",
  "color: mahogany",

  // Metallic / finish tones
  "color: brushed brass",
  "color: satin brass",
  "color: polished nickel",
  "color: bronze",
  "color: copper",
  "color: champagne",
  "color: rose gold",
  "color: pewter",

  // Mineral / natural references
  "color: alabaster",
  "color: oyster",
  "color: pearl",
  "color: driftwood grey",
  "color: flint",
  "color: graphite",
  "color: onyx",
  "color: heather",
  "color: mushroom",
  "color: bone",
  "color: ecru",
  "color: linen",
  "color: flax",
  "color: oatmeal",
];

// ============================================
//  TEXTURE / MATERIAL LIBRARY – cozy, high‑end 2024‑2026 finishes
//  (industrial & avante‑garde removed)
// ============================================
export const TEXTURE_MATERIAL_LIBRARY = [
  // Soft upholstery & throws
  "material: bouclé wool",
  "material: shearling",
  "material: mohair velvet",
  "material: cotton velvet",
  "material: silk velvet",
  "material: performance velvet",
  "material: brushed linen",
  "material: washed linen",
  "material: slubby linen",
  "material: heavyweight linen",
  "material: soft cotton canvas",
  "material: brushed cotton",
  "material: corduroy",
  "material: chenille",
  "material: alpaca wool",
  "material: cashmere",
  "material: hand‑knitted wool",
  "material: chunky knit",
  "material: quilted cotton",
  "material: matelassé",
  "texture: diamond tufting",
  "texture: channel tufting",
  "texture: biscuit tufting",
  "texture: slub weave",
  "texture: nubby bouclé",

  // Leather & hide
  "material: full‑grain leather",
  "material: aniline leather",
  "material: suede",
  "material: nubuck",
  "material: patinated leather",
  "material: shearling hide",
  "material: cowhide",
  "material: shearling‑lined leather",

  // Woods for furniture, floors & walls
  "material: white oak",
  "material: cerused oak",
  "material: fumed oak",
  "material: limed oak",
  "material: ebonized oak",
  "material: walnut",
  "material: teak",
  "material: ash",
  "material: birch",
  "material: maple",
  "material: burl wood veneer",
  "material: rift‑sawn oak",
  "material: hand‑scraped wood",
  "material: wire‑brushed wood",
  "material: reclaimed wood",
  "material: smoked wood",
  "material: thermo‑treated wood",
  "texture: chevron parquet",
  "texture: herringbone pattern",
  "texture: wide plank flooring",
  "texture: finger‑jointed wood",
  "texture: waxed wood",
  "texture: oiled wood",

  // Stone & solid surfaces (refined only)
  "material: honed marble",
  "material: leathered marble",
  "material: polished marble",
  "material: calacatta marble",
  "material: statuary marble",
  "material: travertine",
  "material: limestone",
  "material: soapstone",
  "material: onyx",
  "material: quartzite",
  "material: terrazzo",
  "material: slate",
  "material: basalt",
  "material: pebble terrazzo",
  "texture: honed finish",
  "texture: chiseled edge stone",

  // Plaster & wall finishes (cozy)
  "material: tadelakt plaster",
  "material: venetian plaster",
  "material: roman clay",
  "material: shikkui lime plaster",
  "material: microcement",
  "material: polished plaster",
  "material: burnished plaster",
  "texture: hand‑troweled plaster",
  "texture: tactile wall plaster",
  "texture: soft matte wall",
  "texture: suede effect paint",

  // Tile & ceramics
  "material: zellige tile",
  "material: encaustic cement tile",
  "material: terracotta tile",
  "material: handmade ceramic tile",
  "material: glazed brick",
  "material: subway tile",
  "material: glass mosaic tile",
  "texture: crackle glaze",
  "texture: matte glaze",
  "texture: high‑gloss glaze",
  "texture: irregular hand‑molded edge",

  // Wall treatments
  "material: grasscloth wallpaper",
  "material: silk wallcovering",
  "material: linen wallcovering",
  "material: jute wallcovering",
  "material: suede wall panel",
  "material: upholstered linen panel",
  "material: fluted wood panel",
  "material: reeded wood panel",
  "material: acoustic slat wood panel",
  "material: board‑and‑batten",
  "material: shiplap",
  "material: beadboard",
  "material: limewash finish",

  // Metals & accents (warm & refined)
  "material: unlacquered brass",
  "material: brushed brass",
  "material: satin brass",
  "material: polished nickel",
  "material: aged bronze",
  "material: copper",
  "material: pewter",
  "material: forged iron",
  "material: brushed stainless steel",
  "texture: hammered metal",
  "texture: knurled metal",
  "texture: patinated bronze",
  "texture: brushed gold",

  // Glass
  "material: fluted glass",
  "material: reeded glass",
  "material: ribbed glass",
  "material: frosted glass",
  "material: smoked glass",
  "material: seeded glass",
  "material: slumped glass",
  "material: cast glass",
  "texture: etched glass",
  "texture: sandblasted glass",
  "texture: antiqued mirror",
];

// ============================================
//  WALL FINISH & DECORATIVE TECHNIQUE LIBRARY
// ============================================
export const TECHNIQUE_LIBRARY = [
  "technique: limewash finish",
  "technique: colorwash",
  "technique: stippling",
  "technique: rag rolling",
  "technique: faux marble",
  "technique: trompe l'oeil",
  "technique: sgraffito",
  "technique: fresco",
  "technique: encaustic",
  "technique: antiquing glaze",
  "technique: crackle finish",
  "technique: distressed finish",
  "technique: gold leaf application",
  "technique: silver leaf application",
  "technique: water gilding",
  "technique: verre églomisé",
  "technique: hand-painted mural",
  "technique: mosaic mural",
  "technique: intarsia",
  "technique: marquetry",
  "technique: venetian plaster",
  "technique: marmorino",
  "technique: tadelakt",
  "technique: stucco veneziano",
  "technique: microcement",
  "technique: burnished concrete",
  "technique: polished concrete",
  "technique: shou sugi ban charred wood",
  "technique: yakisugi",
  "technique: cerused wood",
  "technique: limed oak",
  "technique: whitewashed wood",
  "technique: fumed wood",
  "technique: ebonized wood",
  "technique: french polish",
  "technique: oil finish",
  "technique: wax finish",
  "technique: patinated bronze",
  "technique: brushed metal",
  "technique: hammered metal",
  "technique: repoussé",
  "technique: cloisonné",
  "technique: champlevé",
  "technique: ceramic glazing",
  "technique: raku firing",
  "technique: celadon glaze",
  "technique: blue and white porcelain",
];

// ============================================
//  SOFA SHAPE LIBRARY – eclectic geometric variety with construction details
// ============================================
export const SOFA_LIBRARY = [
  "sofa shape: modular sectional with flanged seam cushions",
  "sofa shape: L‑shaped sectional with French seam detail",
  "sofa shape: U‑shaped sectional with boxed seat cushions",
  "sofa shape: curved sectional with sinuous back",
  "sofa shape: tuxedo sofa with high rolled arms and French seams",
  "sofa shape: camelback sofa with serpentine top rail",
  "sofa shape: chesterfield with deep button tufting and nailhead trim",
  "sofa shape: lawson sofa with three loose back cushions and square arms",
  "sofa shape: english roll arm sofa with turned wood legs",
  "sofa shape: track arm sofa with knife‑edge seat cushions",
  "sofa shape: shelter arm sofa with integrated flanged back",
  "sofa shape: tight back sofa with waterfall skirt",
  "sofa shape: pillow back sofa with down‑filled cushions",
  "sofa shape: bench seat sofa with single long cushion",
  "sofa shape: cloud sofa with deep, plump down‑wrapped cushions",
  "sofa shape: bubble sofa with fully rounded organic forms",
  "sofa shape: sculptural sofa with asymmetrical undulating back",
  "sofa shape: monolithic sofa with low profile and thick platform base",
  "sofa shape: mid‑century sofa with splayed tapered legs and tufted back",
  "sofa shape: retro rounded back sofa with cylindrical armrests",
  "sofa shape: 70s low‑profile sofa with wide armrests and platform base",
  "sofa shape: japandi low sofa with slim solid wood frame",
  "sofa shape: wabi sabi organic sofa with irregular hand‑shaped contours",
  "sofa shape: minimalist platform sofa with hidden legs",
  "sofa shape: curved back sofa with kidney‑shaped footprint",
  "sofa shape: serpentine sofa with continuous wave‑like curves",
  "sofa shape: crescent sofa with half‑moon seat plan",
  "sofa shape: asymmetric sofa with one oblique armrest and one open end",
  "sofa shape: deep seat sofa with extra‑deep knife‑edge cushions",
  "sofa shape: channel back sofa with vertical fluted tufting",
  "sofa shape: tufted back sofa with biscuit tufting",
  "sofa shape: slipcovered sofa with relaxed, soft drape",
  "sofa shape: armless sofa with bolster pillows at ends",
  "sofa shape: one‑arm sofa (chaise longue orientation)",
  "sofa shape: bumper sectional with thick, rounded outer corners",
  "sofa shape: wedge sectional with pie‑shaped corner unit",
  "sofa shape: conversation pit sofa with sunken modular arrangement",
  "sofa shape: sunken modular pit sofa with built‑in platform",
  "sofa shape: sofa with trapezoidal arm profiles and slim metal legs",
  "sofa shape: sofa with cylindrical bolster backrest and loose seat cushions",
  "sofa shape: sofa with flanged edge cushions and recessed plinth base",
  "sofa shape: double‑sided sofa with identical front and back faces",
  "sofa shape: curved loveseat with tight upholstery and piping detail",
  "sofa shape: settee with caned back and loose seat cushion",
  "sofa shape: daybed with bolster ends and tailored skirt",
  "sofa shape: chaise lounge with sculpted organic form",
  "sofa shape: ottoman sofa hybrid with low backrest and oversized seat",
];

// ============================================
//  ARMCHAIR SHAPE LIBRARY – unique eclectic designs with geometric terms
// ============================================
export const ARMCHAIR_LIBRARY = [
  "armchair shape: club chair with deep button tufting and rolled arms",
  "armchair shape: wingback chair with sinuous silhouette and tall wings",
  "armchair shape: bergère chair with exposed wood frame and loose cushion",
  "armchair shape: tub chair with curved cocoon back and slim legs",
  "armchair shape: slipper chair with high back and short tapered legs",
  "armchair shape: barrel chair with rounded enveloping back",
  "armchair shape: egg‑shaped lounge chair with swivel base",
  "armchair shape: womb‑style lounge chair with organic shell and steel legs",
  "armchair shape: tulip‑base armchair with pedestal swivel",
  "armchair shape: shell armchair with molded seat and tapered metal legs",
  "armchair shape: bentwood armchair with spindle back and curved top rail",
  "armchair shape: wishbone armchair with Y‑shaped back and woven seat",
  "armchair shape: captain's armchair with low curved back and spindle supports",
  "armchair shape: mid‑century armchair with splayed legs and sculpted armrests",
  "armchair shape: bucket chair with tilted seat and wraparound back",
  "armchair shape: sling chair with leather seat suspended on metal frame",
  "armchair shape: butterfly sling chair with black metal frame and leather sling",
  "armchair shape: accent chair with scalloped back and cabriole legs",
  "armchair shape: scoop chair with continuous curved shell and slim metal base",
  "armchair shape: papasan chair with large round cushion on rattan frame",
  "armchair shape: rattan peacock chair with high fanned back",
  "armchair shape: ball chair with enclosed spherical shell on pedestal",
  "armchair shape: swan chair with continuous sculpted arm‑and‑back form",
  "armchair shape: lounge chair with ottoman and angled backrest",
  "armchair shape: reading chair with high back, winged headrest and deep seat",
  "armchair shape: nursing chair with low curved back and wide seat",
  "armchair shape: office armchair with mid‑back and waterfall seat edge",
  "armchair shape: dining armchair with curved back and upholstered seat",
  "armchair shape: counter stool with low back, footrest and armrests",
  "armchair shape: barstool armchair with full back and swivel seat",
  "armchair shape: bentwood armchair with circular seat and open back",
  "armchair shape: cantilever armchair with tubular steel frame and leather pads",
  "armchair shape: z‑chair with angular metal frame and floating seat",
  "armchair shape: armchair with flanged edge cushions and tapered wood legs",
  "armchair shape: armchair with French seam detailing and tight back",
  "armchair shape: armchair with channel tufted back and flanged arms",
  "armchair shape: armchair with trapezoidal armrests and sled base",
  "armchair shape: armchair with cylindrical bolster arms and deep seat",
  "armchair shape: armchair with sinuous spring base and loose back cushion",
  "armchair shape: armchair with waterfall skirt and boxed seat cushion",
  "armchair shape: armchair with radiused back corners and plinth base",
];

// ============================================
//  FURNITURE LIBRARY – tables, storage, beds, etc. (mid‑century & trendy 2020‑2026)
// ============================================
export const FURNITURE_LIBRARY = [
  // Tables
  "furniture: parsons table with square legs",
  "furniture: trestle table with crossed legs",
  "furniture: pedestal table with central column base",
  "furniture: tulip table with cast aluminum base",
  "furniture: organic shaped coffee table with freeform edges",
  "furniture: coffee table with waterfall edge",
  "furniture: cocktail table with beveled glass top",
  "furniture: end table with drum shape and open shelf",
  "furniture: side table with tripod base",
  "furniture: nesting tables with rounded corners",
  "furniture: c‑table with slide‑under base",
  "furniture: gueridon with marble top and brass base",
  "furniture: console table with demi‑lune shape",
  "furniture: demilune table with reeded front",
  "furniture: dining table with oval top and tapered legs",
  "furniture: round dining table with central pedestal",
  "furniture: extendable dining table with butterfly leaf",
  "furniture: desk with floating top and drawer unit",
  "furniture: writing desk with slim profile and brass pulls",
  "furniture: secretary desk with drop‑front and pigeonholes",

  // Beds & daybeds
  "furniture: platform bed with low profile and integrated nightstands",
  "furniture: upholstered bed with fluted headboard",
  "furniture: canopy bed with slim metal frame",
  "furniture: storage bed with drawers underneath",
  "furniture: daybed with trundle and spindle sides",
  "furniture: four‑poster bed with tapered posts",

  // Storage & shelving
  "furniture: credenza with sliding doors and tapered legs",
  "furniture: sideboard with tambour doors",
  "furniture: buffet with fluted wood front",
  "furniture: hutch with glass upper cabinets",
  "furniture: armoire with arched top and louvered doors",
  "furniture: bookcase with asymmetrical shelves",
  "furniture: modular shelving system with open cubes",
  "furniture: floating shelf with hidden brackets",
  "furniture: étagère with brass frame and glass shelves",
  "furniture: wall‑mounted desk with fold‑down top",
  "furniture: rolling bar cart with glass shelves and metal frame",

  // Mirrors & screens
  "furniture: wall mirror with rounded corners",
  "furniture: floor mirror with thin black frame",
  "furniture: leaning mirror with oversized scale",
  "furniture: arched mirror with brass trim",
  "furniture: folding screen with cane webbing",
  "furniture: room divider with open shelving",
];

// ============================================
//  LIGHTING FIXTURE TYPE
// ============================================
export const LIGHTING_LIBRARY = [
  // Pendants & chandeliers
  "lighting: pendant light with glass globe",
  "lighting: mini pendant with metallic shade",
  "lighting: multi‑light pendant with adjustable arms",
  "lighting: drum pendant with fabric shade",
  "lighting: globe pendant with opal glass",
  "lighting: cone pendant with brass interior",
  "lighting: linear pendant over dining table",
  "lighting: cluster pendant with staggered heights",
  "lighting: sputnik chandelier with brass arms",
  "lighting: beaded chandelier with wood beads",
  "lighting: rattan chandelier with organic shape",
  "lighting: lantern chandelier with glass panels",
  "lighting: contemporary chandelier with slim metal arms",

  // Flush & semi‑flush
  "lighting: flush mount with ribbed glass",
  "lighting: semi‑flush mount with fabric shade",
  "lighting: schoolhouse light with opal glass",

  // Recessed & track
  "lighting: recessed light with trimless finish",
  "lighting: gimbal recessed light adjustable",
  "lighting: picture light with slim brass bar",
  "lighting: undercabinet light strip",

  // Floor lamps
  "lighting: floor lamp with arched arm and marble base",
  "lighting: torchiere with uplight shade",
  "lighting: arc lamp with oversized dome",
  "lighting: pharmacy floor lamp with adjustable arm",
  "lighting: tripod floor lamp with wood legs",
  "lighting: tree floor lamp with multiple adjustable heads",
  "lighting: swing arm floor lamp with brass finish",

  // Table lamps
  "lighting: table lamp with ceramic base",
  "lighting: desk lamp with articulated arm",
  "lighting: banker's lamp with green glass shade",
  "lighting: buffet lamp with slender profile",
  "lighting: ginger jar lamp with silk shade",
  "lighting: gourd lamp with turned wood base",
  "lighting: crystal lamp with faceted body",
  "lighting: glass lamp with ribbed texture",
  "lighting: metal lamp with dome shade",
  "lighting: marble lamp with brass accents",
  "lighting: rattan lamp with woven shade",

  // Wall sconces
  "lighting: wall sconce with backplate",
  "lighting: swing arm sconce with adjustable reach",
  "lighting: picture sconce directing light downward",
  "lighting: wall washer sconce for grazing light",

  // Specialty
  "lighting: LED tape light for cove illumination",
  "lighting: backlit panel with diffuser",
];

// ============================================
//  WINDOW SHAPE / TYPE – basic, high‑end residential
// ============================================
export const WINDOW_LIBRARY = [
  "window: casement window with divided lights",
  "window: double‑hung window with traditional muntins",
  "window: awning window above fixed pane",
  "window: fixed picture window with wide view",
  "window: arched window with keystone detail",
  "window: round oculus window",
  "window: porthole window with brass trim",
  "window: bay window with seat",
  "window: bow window with curved glass",
  "window: oriel window supported by corbels",
  "window: dormer window with pitched roof",
  "window: transom window above door",
  "window: clerestory window for high daylight",
  "window: floor‑to‑ceiling window with slim black frame",
  "window: corner window with mitered glass",
  "window: french door style window opening to balcony",
  "window: sliding glass window with minimal frame",
  "window: sash window with counterweight",
  "window: ribbon window with continuous sill",
  "window: juliet balcony with glass balustrade",
];

// ============================================
//  DOOR TYPE & OPENING – residential, elegant, no industrial
// ============================================
export const DOOR_LIBRARY = [
  "door: panel door with raised molding",
  "door: flush door with hidden hinges",
  "door: glass panel door with frosted glass",
  "door: french door with divided lights",
  "door: pocket door that slides into wall",
  "door: barn door with exposed track",
  "door: sliding door with soft‑close mechanism",
  "door: pivot door with floor‑to‑ceiling height",
  "door: arched door with radius top",
  "door: cased opening with moulding surround",
  "door: bi‑fold door with louvers",
  "door: double door with equal panels",
  "door: dutch door split horizontally",
  "door: shaker door with flat center panel",
  "door: sliding glass door to patio",
  "door: pivot glass door with stainless patch fittings",
  "door: frameless glass door with etched pull",
  "door: wood clad steel entry door",
  "door: juliet balcony door with full glass",
];

// ============================================
//  WALL MOULDING & PANELING
// ============================================
export const MOULDING_LIBRARY = [
  "moulding: crown moulding with cove profile",
  "moulding: tall baseboard with square edge",
  "moulding: picture rail at high level",
  "moulding: chair rail at dado height",
  "moulding: cornice with dentil detail",
  "moulding: architrave around door",
  "moulding: wainscoting with raised panels",
  "moulding: flat panel wainscoting",
  "moulding: beadboard paneling to chair height",
  "moulding: v‑groove paneling on wall",
  "moulding: shiplap on feature wall",
  "moulding: board and batten to ceiling",
  "moulding: grid paneling with square frames",
  "moulding: picture frame moulding on wall",
  "moulding: boiserie paneling with carved details",
  "moulding: fluted wall panel with vertical grooves",
  "moulding: reeded wall panel with narrow flutes",
  "moulding: upholstered wall panel with linen",
  "moulding: acoustic slat wood wall panel",
];

// ============================================
//  CEILING TYPE & SHAPE
// ============================================
export const CEILING_LIBRARY = [
  "ceiling: flat ceiling with smooth finish",
  "ceiling: coffered ceiling with recessed squares",
  "ceiling: barrel vault ceiling with continuous curve",
  "ceiling: tray ceiling with stepped perimeter",
  "ceiling: cove ceiling with indirect lighting",
  "ceiling: beamed ceiling with exposed timber",
  "ceiling: exposed timber beam ceiling with whitewash",
  "ceiling: vaulted ceiling following roof pitch",
  "ceiling: cathedral ceiling with central ridge",
  "ceiling: dome ceiling with oculus",
  "ceiling: slat wood ceiling with linear pattern",
  "ceiling: stretched fabric ceiling with backlight",
  "ceiling: high gloss ceiling reflecting light",
  "ceiling: smooth troweled plaster ceiling",
];

// ============================================
//  FLOORING LIBRARY – hard surfaces only (wood, stone, tile, concrete)
// ============================================
export const FLOORING_LIBRARY = [
  "flooring: wide plank white oak",
  "flooring: herringbone parquet wood",
  "flooring: chevron parquet wood",
  "flooring: versailles parquet pattern",
  "flooring: encaustic cement tile with pattern",
  "flooring: zellige tile floor with irregular surface",
  "flooring: terracotta tile in running bond",
  "flooring: hexagon marble tile",
  "flooring: large format porcelain slab with minimal joints",
  "flooring: bookmatched marble floor with dramatic veining",
  "flooring: polished concrete with a matte sealer",
  "flooring: epoxy terrazzo with marble chips",
  "flooring: limestone floor in honed finish",
  "flooring: slate floor with natural cleft",
  "flooring: brick floor in herringbone pattern",
  "flooring: ceramic tile in matte finish",
  "flooring: porcelain tile with wood‑look",
  "flooring: mosaic tile floor with small squares",
  "flooring: subway tile floor in dark grout",
  "flooring: cement tile with geometric motif",
  "flooring: terrazzo tile with large aggregate",
  "flooring: natural stone slab floor with irregular edges",
  "flooring: marble tile in checkerboard",
  "flooring: travertine tile with filled holes",
  "flooring: resin floor with a satin finish",
  "flooring: cork tile with warm hue",
  "flooring: luxury vinyl plank with oak print",
  "flooring: engineered wood with matte lacquer",
  "flooring: solid wood with wax oil finish",
  "flooring: reclaimed wood with character marks",
  "flooring: hand scraped wood with distressed surface",
  "flooring: oiled wood with natural grain",
  "flooring: stained wood in dark walnut",
  "flooring: fumed wood with darkened tones",
  "flooring: parquet block with border inlay",
];

// ============================================
//  RUG LIBRARY – soft floor coverings
// ============================================
export const RUG_LIBRARY = [
  "rug: hand‑knotted wool rug with traditional motif",
  "rug: silk and wool blend rug with subtle sheen",
  "rug: jute rug with chunky braided texture",
  "rug: sisal rug with tight weave",
  "rug: seagrass rug with natural color",
  "rug: hemp rug with organic feel",
  "rug: alpaca pile rug with deep softness",
  "rug: abaca fiber rug with irregular weave",
  "rug: flatweave cotton dhurrie with stripes",
  "rug: braided jute rug in oval shape",
  "rug: shag wool rug with high pile",
  "rug: tufted wool rug with abstract pattern",
  "rug: high‑low pile rug with carved effect",
  "rug: loop pile rug with texture contrast",
  "rug: cut and loop rug with geometric design",
  "rug: vintage washed rug with faded colors",
  "rug: overdyed rug in jewel tones",
  "rug: hand‑woven undyed wool rug with fringe",
  "rug: moroccan beni ourain rug with diamond pattern",
  "rug: kilim rug with flatwoven tribal design",
  "rug: persian rug with intricate floral border",
  "rug: sheepskin rug with irregular shape",
  "rug: flatweave dhurrie with ikat pattern",
  "rug: saxony carpet with plush cut pile",
  "rug: velvet carpet with dense soft surface",
];

// ============================================
//  HARDWARE / DETAIL – warm, refined accents
// ============================================
export const HARDWARE_LIBRARY = [
  "hardware: cabinet knob in brass",
  "hardware: bar pull in matte black",
  "hardware: T‑bar pull in satin nickel",
  "hardware: finger pull edge profile",
  "hardware: cup pull in bronze",
  "hardware: concealed hinge for flush doors",
  "hardware: decorative hinge with ball finial",
  "hardware: brass nailhead trim on upholstery",
  "hardware: leather pull tab",
  "hardware: recessed gola profile handle",
  "hardware: touch latch for push‑to‑open doors",
  "hardware: soft‑close drawer slide",
  "hardware: lever handle in unlacquered brass",
  "hardware: door knob in polished nickel",
  "hardware: floor stop in solid brass",
  "hardware: pivot hinge for heavy door",
  "hardware: handrail bracket in wrought iron",
  "hardware: finial for curtain rod",
  "hardware: curtain rod in aged bronze",
  "hardware: drapery ring with clip",
  "hardware: picture hook in brass",
  "hardware: shelf bracket with decorative scroll",
  "hardware: corbel supporting open shelf",
  "hardware: appliance pull in stainless steel",
  "hardware: vent grille with linear pattern",
  "hardware: floor register in cast iron",
  "hardware: ceiling diffuser in white",
  "hardware: linear slot diffuser in black",
];

// ============================================
//  INDOOR PLANT – architectural & editorial favourites
// ============================================
export const PLANT_LIBRARY = [
  "plant: fiddle leaf fig (ficus lyrata)",
  "plant: monstera deliciosa with large fenestrated leaves",
  "plant: philodendron with cascading vines",
  "plant: pothos with trailing stems",
  "plant: snake plant (sansevieria) upright leaves",
  "plant: ZZ plant with glossy leaflets",
  "plant: peace lily with white spathes",
  "plant: rubber plant (ficus elastica) burgundy leaves",
  "plant: bird of paradise with broad tropical leaves",
  "plant: dracaena marginata with spiky foliage",
  "plant: bamboo palm with feathery fronds",
  "plant: areca palm with arching stems",
  "plant: kentia palm with elegant drooping leaflets",
  "plant: yucca cane with sharp leaves",
  "plant: aloe vera with fleshy rosettes",
  "plant: echeveria succulent rosette",
  "plant: string of pearls cascading succulent",
  "plant: jade plant with thick oval leaves",
  "plant: air plant (tillandsia) on stand",
  "plant: staghorn fern mounted on board",
  "plant: bird's nest fern with wavy fronds",
  "plant: maidenhair fern delicate foliage",
  "plant: calathea orbifolia with round striped leaves",
  "plant: prayer plant (maranta) with red veins",
  "plant: alocasia amazonica with arrow‑shaped leaves",
  "plant: anthurium with glossy red spathes",
  "plant: moth orchid (phalaenopsis) in pot",
  "plant: bromeliad with colorful center",
  "plant: olive tree indoor in large planter",
  "plant: citrus tree (lemon) in terracotta pot",
  "plant: bonsai ficus with twisted trunk",
  "plant: money tree (pachira) with braided stem",
  "plant: hanging planter with cascading ivy",
];

// ============================================
//  DECOR OBJECT / ACCESSORY – refined, cozy
// ============================================
export const DECOR_LIBRARY = [
  "decor: oversized round wall mirror with brass frame",
  "decor: sunburst mirror with gold rays",
  "decor: antiqued mirror with mottled glass",
  "decor: sculptural vase with organic form",
  "decor: bud vase with single stem",
  "decor: ceramic urn with lid",
  "decor: glass cloche on wooden base",
  "decor: terrarium with miniature plants",
  "decor: pillar candle on brass holder",
  "decor: decorative tray with marble surface",
  "decor: catch‑all bowl in hand‑thrown clay",
  "decor: coffee table book stack",
  "decor: framed art print with wide mat",
  "decor: gallery wall of mixed frames",
  "decor: leaning artwork against wall",
  "decor: sculptural object in polished alabaster",
  "decor: marble bookend pair",
  "decor: geode specimen on stand",
  "decor: ikebana arrangement in shallow vessel",
  "decor: woven basket with lid",
  "decor: throw blanket in chunky knit",
  "decor: accent pillow with lumbar shape",
  "decor: floor cushion in velvet",
  "decor: sheepskin throw draped on chair",
  "decor: wall tapestry with abstract weave",
  "decor: macramé wall hanging",
  "decor: wall clock with minimal face",
  "decor: globe on brass stand",
  "decor: bust sculpture in white plaster",
  "decor: architectural fragment as art",
  "decor: glass vase with ribbed texture",
  "decor: porcelain vase with blue glaze",
  "decor: stoneware vase in matte black",
  "decor: terracotta pot with saucer",
  "decor: cachepot in woven rattan",
  "decor: hanging planter in macramé",
  "decor: apothecary jar with cork stopper",
  "decor: glass decanter with brass tag",
  "decor: carafe and tumbler set",
  "decor: fruit bowl in hand‑blown glass",
  "decor: cake stand with glass dome",
  "decor: salt and pepper shaker set in marble",
  "decor: pepper mill in wood",
  "decor: teapot in cast iron",
  "decor: french press coffee maker",
  "decor: espresso cup with saucer",
  "decor: tea cup and saucer with floral motif",
  "decor: paperweight in clear resin",
  "decor: waste basket in woven seagrass",
  "decor: umbrella stand in metal",
  "decor: coat hook in brass",
  "decor: ottoman tray in leather",
  "decor: picture frame in thin black wood",
  "decor: shadow box with mementos",
  "decor: bar cart accessory set",
  "decor: ice bucket in hammered metal",
  "decor: cocktail shaker in copper",
  "decor: decanter set with crystal stopper",
  "decor: coaster set in agate",
  "decor: table runner in linen",
  "decor: napkin ring in brass",
  "decor: trivet in cast iron",
  "decor: hourglass with sand",
  "decor: magnifying glass with brass handle",
  "decor: jewelry box in lacquered wood",
  "decor: valet tray in leather",
  "decor: watering can in galvanized steel",
  "decor: candle lantern with glass panels",
  "decor: tea light holder in colored glass",
  "decor: wreath of dried eucalyptus",
  "decor: garland of felt leaves",
];

// ============================================
//  WALL ART SPECIFIC LIBRARY – used when "includeArt" is true
// ============================================
export const WALL_ART_LIBRARY = [
  "wall art: large abstract oil painting with muted earth tones",
  "wall art: minimalist line drawing in black frame",
  "wall art: vintage botanical print with brass frame",
  "wall art: black‑and‑white architectural photograph",
  "wall art: oversized color‑field canvas in deep indigo",
  "wall art: set of three geometric watercolors",
  "wall art: antique landscape etching in gilt frame",
  "wall art: serene seascape photograph in slim black frame",
  "wall art: mid‑century abstract composition in mustard and teal",
  "wall art: charcoal life drawing in a simple oak frame",
  "wall art: gallery wall of mixed vintage frames",
  "wall art: large macramé wall hanging with geometric pattern",
  "wall art: sculptural metal wall piece with brushed brass finish",
  "wall art: Japanese ukiyo‑e print in dark wood frame",
  "wall art: impressionist floral still life in ornate frame",
  "wall art: contemporary typographic print with inspirational quote",
  "wall art: hand‑painted ceramic tile mural",
  "wall art: woven fiber art piece with earthy fibers",
  "wall art: oversized round mirror with leather strap",
  "wall art: minimalist photographic diptych in thin white frames",
  "wall art: 1970s retro travel poster in warm oranges and browns",
  "wall art: black‑and‑white street photography in gallery mount",
  "wall art: batik fabric wall hanging with indigo patterns",
  "wall art: encaustic abstract with textured layers of cream and rust",
  "wall art: large vintage map with aged parchment tones",
  "wall art: neon sign with a single soft‑glowing word",
  "wall art: driftwood and yarn woven wall sculpture",
  "wall art: fine‑art nude photograph in dark walnut frame",
  "wall art: cubist still life print in thin black frame",
  "wall art: abstract expressionist canvas with energetic brushstrokes",
  "wall art: surrealist dreamscape in a silver leaf frame",
  "wall art: collage of vintage sheet music and handwritten letters",
  "wall art: pencil sketch of a historic building in black frame",
  "wall art: watercolor study of local flora with deckled edges",
  "wall art: art deco poster with gilded geometric patterns",
  "wall art: screen printed concert poster in vibrant colors",
  "wall art: hand‑embroidered hoop art in a shadow box",
  "wall art: framed pressed flower composition",
  "wall art: cyanotype of botanical specimens on handmade paper",
  "wall art: monochrome landscape photograph with deep contrast",
  "wall art: abstract geometric print with gold foil accents",
  "wall art: limited edition risograph print with layered colors",
  "wall art: large‑scale graffiti‑inspired canvas",
  "wall art: framed antique kimono fragment",
  "wall art: gallery‑wrapped canvas of a misty forest scene",
  "wall art: trio of black‑framed minimal architecture photographs",
  "wall art: oversized abstract marbleized paper art",
  "wall art: porcelain plate wall arrangement with floral motifs",
  "wall art: hand‑painted shield and crest in dark wood frame",
  "wall art: minimalist ink wash landscape scroll",
  "wall art: salvaged stained glass panel with backlight",
  "wall art: painted recycled wood plank with abstract design",
  "wall art: abstract aerial photograph of a river delta",
  "wall art: framed collection of vintage coins and stamps",
  "wall art: encased butterfly specimen display",
  "wall art: beaded wall hanging with tribal motifs",
  "wall art: hand‑carved wooden mask on a stand",
  "wall art: braided rope wall hanging with driftwood bar",
  "wall art: 3D paper art framed in a deep shadow box",
  "wall art: dyed silk scarf stretched as wall art",
  "wall art: large‑format silver print of an iconic mid‑century home",
  "wall art: brass‑framed convex mirror with aged patina",
  "wall art: framed vintage movie poster with saturated colors",
  "wall art: relief print series of local birds in a shared frame",
  "wall art: galvanized metal wall sculpture with cut‑out pattern",
  "wall art: charcoal triptych of life‑size figures",
  "wall art: large‑scale digital print of an imaginary landscape",
  "wall art: mirrored wall panel with etched geometric lines",
  "wall art: framed original architectural blueprint",
  "wall art: accordion‑folded photograph book displayed open",
  "wall art: resin‑embedded flower wall piece with LED backlight",
];

// ============================================
//  UTILITY: remove label prefix (e.g., "color: ..." → "...")
// ============================================
export const stripLabel = (labeledStr) => labeledStr.replace(/^[^:]+:\s*/, '');

// ============================================
//  BUILD RANDOM TEXTURE DESCRIPTION
//  Returns a short phrase like "honed marble in warm ivory"
// ============================================
export const buildRandomTextureDescription = () => {
  const allMaterials = [
    ...TEXTURE_MATERIAL_LIBRARY,
    ...CUSTOM_MATERIALS,
  ];
  const allColors = [
    ...COLOR_LIBRARY,
    ...CUSTOM_COLORS,
  ];

  const material = allMaterials[Math.floor(Math.random() * allMaterials.length)];
  const color = allColors[Math.floor(Math.random() * allColors.length)];

  return `${stripLabel(material)} in ${stripLabel(color)}`;
};

// ============================================
//  BUILD RANDOM SCENE DESCRIPTION (no truncation)
//  Incorporates doors, moulding, hardware, plants, art,
//  windows, lighting, ceiling, wall finish techniques,
//  and a decor accent for maximum variety.
//  Uses separated sofa, armchair, rug, flooring libraries.
// ============================================
export const buildRandomSceneDescription = (options = {}) => {
  const {
    includeRug = false,
    includeFlooring = false,
    includeArt = false,
    includePaintedWalls = false,
    includeCeiling = false,
    includeMoulding = false,
    includeLighting = false,
    includeDoor = false,
    includeDecor = false,
    includeTechnique = false,
    includeHardware = false,
  } = options;

  // Mandatory items: sofa, coffee table, end table, armchair(s), window, plant
  const allSofas = [...SOFA_LIBRARY, ...CUSTOM_FURNITURE.filter(f => f.startsWith('sofa'))];
  const sofa = allSofas[Math.floor(Math.random() * allSofas.length)];

  const coffeeTables = FURNITURE_LIBRARY.filter(f =>
    f.includes('coffee table') || f.includes('cocktail table')
  );
  const coffeeTable = coffeeTables[Math.floor(Math.random() * coffeeTables.length)];

  const endTables = FURNITURE_LIBRARY.filter(f =>
    f.includes('end table') || f.includes('side table') || f.includes('c‑table') || f.includes('gueridon')
  );
  const endTable = endTables[Math.floor(Math.random() * endTables.length)];

  const armchairs = [...ARMCHAIR_LIBRARY];
  const numChairs = Math.floor(Math.random() * 2) + 1;
  const chairItem = armchairs[Math.floor(Math.random() * armchairs.length)];
  const chairPhrase = numChairs === 1
    ? `a (Armchair Shape: ${stripLabel(chairItem)})`
    : `two matching (Armchair Shape: ${stripLabel(chairItem)})s`;   // parentheses around the shape

  const windowItem = WINDOW_LIBRARY[Math.floor(Math.random() * WINDOW_LIBRARY.length)];
  const plantItem = PLANT_LIBRARY[Math.floor(Math.random() * PLANT_LIBRARY.length)];

  const parts = [];

  // Core structure with parentheses labels
  parts.push(`A (Sofa Shape: ${stripLabel(sofa)})`);
  parts.push(chairPhrase);
  parts.push(`with a (Furniture: ${stripLabel(coffeeTable)})`);
  parts.push(`and a (Furniture: ${stripLabel(endTable)})`);
  parts.push(`beside a (Window: ${stripLabel(windowItem)})`);
  parts.push(`near a (Plant: ${stripLabel(plantItem)})`);

  // Optional rug
  if (includeRug) {
    const rugItem = RUG_LIBRARY[Math.floor(Math.random() * RUG_LIBRARY.length)];
    parts.push(`on a (Rug: ${stripLabel(rugItem)})`);
  }

  // Optional flooring
  if (includeFlooring) {
    const flooringItem = FLOORING_LIBRARY[Math.floor(Math.random() * FLOORING_LIBRARY.length)];
    parts.push(`with (Flooring: ${stripLabel(flooringItem)})`);
  }

  if (includeArt) {
    const art = WALL_ART_LIBRARY[Math.floor(Math.random() * WALL_ART_LIBRARY.length)];
    parts.push(`with (Wall Art: ${stripLabel(art)}) on the wall`);
  }
  if (includePaintedWalls) {
    const wallColor = COLOR_LIBRARY[Math.floor(Math.random() * COLOR_LIBRARY.length)];
    parts.push(`walls painted (Color: ${stripLabel(wallColor)})`);
  }
  if (includeCeiling) {
    const ceiling = CEILING_LIBRARY[Math.floor(Math.random() * CEILING_LIBRARY.length)];
    parts.push(`under a (Ceiling: ${stripLabel(ceiling)})`);
  }
  if (includeMoulding) {
    const moulding = MOULDING_LIBRARY[Math.floor(Math.random() * MOULDING_LIBRARY.length)];
    parts.push(`with (Moulding: ${stripLabel(moulding)}) detailing`);
  }
  if (includeLighting) {
    const lighting = LIGHTING_LIBRARY[Math.floor(Math.random() * LIGHTING_LIBRARY.length)];
    parts.push(`illuminated by a (Lighting: ${stripLabel(lighting)})`);
  }
  if (includeDoor) {
    const door = DOOR_LIBRARY[Math.floor(Math.random() * DOOR_LIBRARY.length)];
    parts.push(`near a (Door: ${stripLabel(door)})`);
  }
  if (includeDecor) {
    const decor = DECOR_LIBRARY[Math.floor(Math.random() * DECOR_LIBRARY.length)];
    parts.push(`with a (Decor: ${stripLabel(decor)})`);
  }
  if (includeTechnique) {
    const technique = TECHNIQUE_LIBRARY[Math.floor(Math.random() * TECHNIQUE_LIBRARY.length)];
    parts.push(`walls finished with (Technique: ${stripLabel(technique)})`);
  }
  if (includeHardware) {
    const hardware = HARDWARE_LIBRARY[Math.floor(Math.random() * HARDWARE_LIBRARY.length)];
    parts.push(`featuring (Hardware: ${stripLabel(hardware)}) accents`);
  }

  let sentence = parts.join(', ') + '.';
  sentence = sentence.replace(/,\s*,/g, ',').replace(/\s+/g, ' ').trim();
  sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1);

  return sentence;
};